"use client";

import { useEffect, useState } from "react";
import clsx from "clsx";
import { Compass } from "lucide-react";

interface TocItem {
  id: string;
  text: string;
  level: number;
}

export function Toc() {
  const [headings, setHeadings] = useState<TocItem[]>([]);
  const [activeId, setActiveId] = useState<string>("");

  useEffect(() => {
    const elements = Array.from(document.querySelectorAll("h2, h3"))
      .filter((element) => element.id);
    
    const tocItems: TocItem[] = elements.map((element) => ({
      id: element.id,
      text: element.textContent || "",
      level: Number(element.tagName.charAt(1)),
    }));

    setHeadings(tocItems);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: "0% 0% -80% 0%" }
    );

    elements.forEach((element) => observer.observe(element));

    return () => {
      elements.forEach((element) => observer.unobserve(element));
    };
  }, []);

  if (headings.length === 0) {
    return null;
  }

  return (
    <div className="hidden xl:block w-[200px] shrink-0 pt-2 text-sm">
      <div className="sticky top-24">
        <div className="hide-scrollbar max-h-[calc(100vh-8rem)] overflow-x-hidden overflow-y-auto py-4 px-4">
          <div className="mb-3 flex items-center gap-2.5 px-1">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#eff3ff] text-[#335AFF] dark:bg-[#335AFF]/16 dark:text-[#8da4ff]">
              <Compass className="h-4 w-4" />
            </div>
            <p className="text-sm font-semibold text-[#0f172a] dark:text-[#f8fafc]">本页目录</p>
          </div>

          <nav className="flex min-w-0 flex-col space-y-0.5 border-l border-[#d9e3f3] pl-3 dark:border-[#334155]">
          {headings.map((heading) => {
            const isActive = activeId === heading.id;
            const isSubHeading = heading.level === 3;

            return (
            <a
              key={heading.id}
              href={`#${heading.id}`}
              className={clsx(
                "group flex min-w-0 items-start gap-2.5 rounded-lg py-1.5 pr-2 transition-all duration-200",
                isSubHeading ? "pl-3 text-xs" : "pl-0 text-[13px]",
                isActive
                  ? "text-[#335AFF] dark:text-[#8da4ff]"
                  : "text-[#6b7280] dark:text-[#94a3b8] hover:text-[#0f172a] dark:hover:text-[#f8fafc]"
              )}
            >
              <span
                className={clsx(
                  "mt-[0.42rem] flex-shrink-0 rounded-full transition-colors",
                  isSubHeading ? "h-1.5 w-1.5" : "h-2 w-2",
                  isActive
                    ? isSubHeading
                      ? "bg-[#7c8fb0] dark:bg-[#94a3b8]"
                      : "bg-[#335AFF] dark:bg-[#8da4ff]"
                    : isSubHeading
                      ? "bg-[#cfd8e6] group-hover:bg-[#94a3b8] dark:bg-[#475569] dark:group-hover:bg-[#94a3b8]"
                      : "bg-[#bcc8d9] group-hover:bg-[#7c8fb0] dark:bg-[#475569] dark:group-hover:bg-[#94a3b8]"
                )}
              />
              <span className={clsx("min-w-0 flex-1 truncate leading-5", isActive && "font-semibold")}>{heading.text}</span>
            </a>
            );
          })}
          </nav>
        </div>
      </div>
    </div>
  );
}
